import { Component, h, Host, Prop, Event, Method, State, Element, Watch } from '@stencil/core';
import { generateRandomNumber, getInheritedBackgroundColor, hideFocusRingWhenUsingMouse } from '../../../global/utils/utils';
/**
 * @slot hint-text - Used for the inclusion of Phrasing content (see https://www.w3.org/TR/2012/WD-html-markup-20121025/common-models.html) eg. an anchor tag in the sub heading.
 * There is also a prop to pass text.
 */
export class TextBoxComponent {
  constructor() {
    /**
     * The input ID
     */
    this.textBoxId = generateRandomNumber();
    /**
     * The label text for the input, will be hidden if type is experiential
     * TODO This needs to be mandatory in order to be accessible
     */
    this.label = '';
    /**
     * The type property of the input e.g. text, email, number
     */
    this.inputType = 'text';
    /**
     * The browsers autocomplete property of the input
     */
    this.browserAutocomplete = true;
    /**
     * Flag to show error state
     */
    this.hasError = false;
    /**
     * Flag to show valid state
     */
    this.isInputValid = false;
    /**
     * Text to show when valid
     */
    this.validText = '';
    /**
     * Type for the control, either default, default-inverse or experiential
     */
    this.type = 'default';
  }
  valueWatchHandler(newValue) {
    this.inputValue = newValue;
  }
  /**
   * Public method to focus on the input
   */
  async focusInput() {
    this.inputElement.focus();
  }
  /**
   * Public method to trigger blur event for the input
   */
  async blurInput() {
    this.inputElement.blur();
  }
  /**
   * Public method to clear the input
   */
  async clearInput() {
    this.inputValue = '';
  }
  /**
   * Public method to clear the input
   */
  async setInput(textValue) {
    this.inputValue = textValue;
  }
  /**
   * Public method to select the input
   */
  async selectInput() {
    this.inputElement.select();
  }
  /**
   * Public method to retrieve the input dom element
   */
  async getInputElement() {
    return this.inputElement;
  }
  /**
   * Sets focus to the textbox
   */
  async setFocus() {
    this.inputElement.focus();
  }
  componentWillLoad() {
    if (!this.label) {
      // TODO change this throw an error. It will be a breaking change.
      console.warn(`The "label" prop for ${this.textBoxId} is mandatory: please provide a value.`);
    }
    this.inputValue = this.value;
  }
  componentWillRender() {
    this.host.style.setProperty('--textbox-background-color', getInheritedBackgroundColor(this.host));
  }
  componentDidLoad() {
    hideFocusRingWhenUsingMouse(this.inputElement);
  }
  focusHandler(event) {
    this.inputFocused = true;
    this.textboxFocus.emit(event);
  }
  blurHandler(event) {
    this.inputFocused = false;
    this.textboxBlur.emit(event);
  }
  inputHandler(event) {
    this.inputValue = event.target.value;
    this.textboxInput.emit(event);
  }
  keyDownHandler(event) {
    this.textboxKeydown.emit(event);
  }
  changeHandler(event) {
    const val = event.target && event.target.value;
    this.value = val; //needs to be set for ngmodel and formcontrolname
    this.valueChange.emit(event.target.value); // emit the value for the valuechange event
  }
  renderHintText() {
    var _a, _b;
    if (this.isInputValid) {
      return h("div", { slot: "hint-text" }, this.validText);
    }
    else if (((_a = this.hintText) === null || _a === void 0 ? void 0 : _a.length) > 0 || ((_b = this.host.querySelector('[slot="hint-text"]')) === null || _b === void 0 ? void 0 : _b.innerHTML.length) > 0) {
      return h("div", { slot: "hint-text" }, this.hintText ? this.hintText : h("slot", { name: "hint-text" }));
    }
    return '';
  }
  render() {
    let classPrefix;
    switch (this.type) {
      case 'default':
      case 'default-inverse':
        classPrefix = 'agl-ds-textbox-default';
        break;
      case 'experiential':
        classPrefix = 'agl-ds-textbox-experiential';
        break;
      default:
        classPrefix = 'agl-ds-textbox-default';
        break;
    }
    const isInputActive = this.inputFocused;
    const inputHasValue = !!this.inputValue;
    return (h(Host, null,
      h("div", { class: classPrefix },
        h("div", { class: `${classPrefix}__container` },
          h("label", { class: {
              [`${classPrefix}__label`]: true,
              [`${classPrefix}__label--has-value`]: inputHasValue,
              [`${classPrefix}__label--active`]: isInputActive || inputHasValue,
              [`${classPrefix}__label--error`]: this.hasError,
              ['sr-only']: this.type === 'experiential'
            }, htmlFor: this.textBoxId }, this.label),
          h("input", { class: {
              [`${classPrefix}__input--default`]: this.type === 'default' || this.type === 'experiential',
              [`${classPrefix}__input--inverse`]: this.type === 'default-inverse',
              [`${classPrefix}__input--active`]: isInputActive,
              [`${classPrefix}__input--valid`]: this.isInputValid && !this.hasError,
              [`${classPrefix}__input--error`]: this.hasError
            }, type: this.inputType, id: this.textBoxId, name: this.textBoxId, onBlur: (e) => this.blurHandler(e), onInput: (e) => this.inputHandler(e), onKeyDown: (e) => this.keyDownHandler(e), onFocus: (e) => this.focusHandler(e), onChange: (e) => this.changeHandler(e), value: this.inputValue, placeholder: this.type === 'experiential' ? this.placeholder : undefined, maxLength: this.maxLength, autoComplete: this.browserAutocomplete ? undefined : 'off', ref: (el) => (this.inputElement = el), "aria-describedBy": this.textBoxId, "aria-invalid": `${this.hasError}` }),
          this.type !== 'experiential' && !this.hasError && (h("span", { class: {
              [`${classPrefix}__bar`]: true,
              [`${classPrefix}__bar--active`]: isInputActive
            } })),
          this.suffixIcon &&
            !this.hasError && [
            h("span", { innerHTML: this.suffixIcon, onClick: (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                this.suffixIconClick.emit();
              }, class: `${classPrefix}__suffix-icon`, "aria-hidden": "true" })
          ],
          this.hasError && [
            h("span", { class: {
                'input-overlay': true,
                ['input-overlay--inverse']: this.type === 'default-inverse',
                ['input-overlay--experiential']: this.type === 'experiential'
              }, "aria-describedBy": `ErrorId ${this.textBoxId}` }),
            h("svg", { class: `${classPrefix}__error-icon`, focusable: "false", "aria-hidden": "true", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24" },
              h("rect", { width: "24", height: "24", fill: "none" }),
              h("path", { d: "M12,2.5,1,21.5H23Zm1,16H11v-2h2Zm-2-4v-4h2v4Z" }))
          ],
          this.isInputValid && !this.hasError && (h("svg", { class: {
              'text-input__icon': true,
              ['text-input__icon--experiential']: this.type === 'experiential'
            }, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", width: "24", height: "24" },
            h("path", { d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" })))),
        h("agl-ds-hint-validation-message", { asDescribedbyId: this.textBoxId, hasError: this.hasError },
          h("div", { slot: "validation-text" }, this.validationText),
          this.renderHintText()))));
  }
  static get is() { return "agl-ds-textbox"; }
  static get encapsulation() { return "scoped"; }
  static get originalStyleUrls() { return {
    "$": ["textbox.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["textbox.component.css"]
  }; }
  static get properties() { return {
    "textBoxId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": "The input ID"
      },
      "attribute": "text-box-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label text for the input, will be hidden if type is experiential\nTODO This needs to be mandatory in order to be accessible"
      },
      "attribute": "label",
      "reflect": false,
      "defaultValue": "''"
    },
    "value": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The value for the input"
      },
      "attribute": "value",
      "reflect": false
    },
    "inputType": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "InputType",
        "resolved": "\"email\" | \"number\" | \"search\" | \"tel\" | \"text\" | \"url\"",
        "references": {
          "InputType": {
            "location": "import",
            "path": "./textbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The type property of the input e.g. text, email, number"
      },
      "attribute": "input-type",
      "reflect": false,
      "defaultValue": "'text'"
    },
    "placeholder": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The placeholder property for the input, only used if type is experiential"
      },
      "attribute": "placeholder",
      "reflect": false
    },
    "maxLength": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": "The maxlength property of the input"
      },
      "attribute": "max-length",
      "reflect": false
    },
    "browserAutocomplete": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The browsers autocomplete property of the input"
      },
      "attribute": "browser-autocomplete",
      "reflect": false,
      "defaultValue": "true"
    },
    "hasError": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show error state"
      },
      "attribute": "has-error",
      "reflect": false,
      "defaultValue": "false"
    },
    "isInputValid": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show valid state"
      },
      "attribute": "is-input-valid",
      "reflect": false,
      "defaultValue": "false"
    },
    "validText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Text to show when valid"
      },
      "attribute": "valid-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "TextboxType",
        "resolved": "\"default\" | \"default-inverse\" | \"experiential\"",
        "references": {
          "TextboxType": {
            "location": "import",
            "path": "./textbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Type for the control, either default, default-inverse or experiential"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "validationText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Validation text to show when the has error flag is true"
      },
      "attribute": "validation-text",
      "reflect": false
    },
    "hintText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Hint text will be shown underneath the textbox but will be hidden if there is an error. There is also a slot which can be used for the inclusion of Phrasing content\n(see https://www.w3.org/TR/2012/WD-html-markup-20121025/common-models.html#common.elem.phrasing) eg. an anchor tag in the sub heading. There is also a prop to pass text."
      },
      "attribute": "hint-text",
      "reflect": false
    },
    "suffixIcon": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "An custom icon inside of the textbox e.g. calendar"
      },
      "attribute": "suffix-icon",
      "reflect": false
    }
  }; }
  static get states() { return {
    "inputFocused": {},
    "inputValue": {}
  }; }
  static get events() { return [{
      "method": "textboxBlur",
      "name": "textboxBlur",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Forwards the blur event of the input"
      },
      "complexType": {
        "original": "FocusEvent",
        "resolved": "FocusEvent",
        "references": {
          "FocusEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "textboxFocus",
      "name": "textboxFocus",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Forwards the focus event of the input"
      },
      "complexType": {
        "original": "FocusEvent",
        "resolved": "FocusEvent",
        "references": {
          "FocusEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "textboxInput",
      "name": "textboxInput",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Forwards the input event of the input"
      },
      "complexType": {
        "original": "InputEvent",
        "resolved": "InputEvent",
        "references": {
          "InputEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "textboxKeydown",
      "name": "textboxKeydown",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Forwards the keydown event of the input"
      },
      "complexType": {
        "original": "KeyboardEvent",
        "resolved": "KeyboardEvent",
        "references": {
          "KeyboardEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "suffixIconClick",
      "name": "suffixIconClick",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Click event fired when clicking on the suffix icon"
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "valueChange",
      "name": "valueChange",
      "bubbles": false,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Text box value changed event"
      },
      "complexType": {
        "original": "any",
        "resolved": "any",
        "references": {}
      }
    }]; }
  static get methods() { return {
    "focusInput": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to focus on the input",
        "tags": []
      }
    },
    "blurInput": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to trigger blur event for the input",
        "tags": []
      }
    },
    "clearInput": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to clear the input",
        "tags": []
      }
    },
    "setInput": {
      "complexType": {
        "signature": "(textValue: string) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to clear the input",
        "tags": []
      }
    },
    "selectInput": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to select the input",
        "tags": []
      }
    },
    "getInputElement": {
      "complexType": {
        "signature": "() => Promise<HTMLInputElement>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLInputElement": {
            "location": "global"
          }
        },
        "return": "Promise<HTMLInputElement>"
      },
      "docs": {
        "text": "Public method to retrieve the input dom element",
        "tags": []
      }
    },
    "setFocus": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Sets focus to the textbox",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "value",
      "methodName": "valueWatchHandler"
    }]; }
}
